import { useEffect, useMemo, useState } from "react";
import { useVault } from "./hooks/useVault.jsx";
import { useToast } from "./hooks/useToast.jsx";
import { useTheme } from "./hooks/useTheme.js";

import { LockScreen } from "./views/LockScreen.jsx";
import { Sidebar, SECTIONS } from "./components/Sidebar.jsx";
import { EntryList } from "./components/EntryList.jsx";
import { MiniCalendar } from "./components/MiniCalendar.jsx";
import { Editor } from "./components/Editor.jsx";
import { EmptyState } from "./components/EmptyState.jsx";
import { SettingsDialog } from "./views/Settings.jsx";
import { AskAIView } from "./views/AskAI.jsx";
import { FinancialsView } from "./views/Financials.jsx";
import { GoalsView } from "./views/Goals.jsx";

import { toInputValue, fmtDate } from "./lib/dates.js";
import * as Notify from "./lib/notify.js";
import {
  SearchIcon, PlusIcon, SunIcon, MoonIcon, MenuIcon, BellIcon, CalendarIcon,
} from "./components/Icons.jsx";

const NOTE_SECTIONS = ["all", "diary", "journal", "voice", "password", "alerts"];
const VIEW_MODES = [
  { id: "list", label: "List" },
  { id: "thumbnails", label: "Cards" },
  { id: "icons", label: "Icons" },
  { id: "large", label: "Large" },
];

export default function App() {
  const vault = useVault();
  const toast = useToast();
  const { theme, toggle: toggleTheme } = useTheme();

  const [section, setSection] = useState("all");
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("aidia.view") || "list");
  const [editing, setEditing] = useState(null); // {} for new, entry for edit
  const [showSettings, setShowSettings] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => localStorage.setItem("aidia.view", view), [view]);

  // Schedule OS-level reminders whenever the alerting entries change.
  useEffect(() => {
    if (!vault.unlocked) return;
    Notify.init?.();
    Notify.syncAll?.(vault.entries.filter((e) => e.alertEnabled && e.alertDate)).catch(() => {});
  }, [vault.unlocked, vault.entries]);

  // --- derived data --------------------------------------------------------
  const dueEntries = useMemo(() => {
    const today = toInputValue(new Date());
    return vault.entries
      .filter((e) => e.alertEnabled && e.alertDate && e.alertDate <= today)
      .sort((a, b) => a.alertDate.localeCompare(b.alertDate));
  }, [vault.entries]);

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of vault.entries) {
      const key = toInputValue(new Date(e.createdAt || e.updatedAt || Date.now()));
      (map[key] ||= []).push(e);
    }
    return map;
  }, [vault.entries]);

  const counts = useMemo(() => {
    const c = { alerts: dueEntries.length, all: vault.entries.length };
    for (const cat of ["diary", "journal", "voice", "password"]) {
      c[cat] = vault.entries.filter((e) => e.category === cat).length;
    }
    c.goals = vault.goals.length;
    c.financials = vault.transactions.length;
    return c;
  }, [vault.entries, vault.goals, vault.transactions, dueEntries]);

  const visibleEntries = useMemo(() => {
    let list = vault.entries;

    if (section === "alerts") list = dueEntries;
    else if (NOTE_SECTIONS.includes(section) && section !== "all") {
      list = list.filter((e) => e.category === section);
    }

    // The mini calendar on Diary/Journal narrows to one day.
    if (selectedDay && (section === "diary" || section === "journal" || section === "calendar")) {
      list = list.filter(
        (e) => toInputValue(new Date(e.createdAt || e.updatedAt || 0)) === selectedDay
      );
    }

    const q = query.trim().toLowerCase();
    if (q) {
      const pool = scope === "all" ? list : list.filter((e) => e.category === scope);
      list = pool.filter((e) =>
        [e.title, e.content, e.username, e.url, ...(e.tags || [])]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }

    return [...list].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [vault.entries, dueEntries, section, selectedDay, query, scope]);

  // --- chrome --------------------------------------------------------------
  const meta = SECTIONS.find((s) => s.id === section);
  const showsNotes = NOTE_SECTIONS.includes(section);
  const sideCalendar = section === "diary" || section === "journal";

  return (
    <div className="app">
      <Sidebar
        section={section}
        onSection={(id) => { setSection(id); setNavOpen(false); setSelectedDay(null); }}
        counts={counts}
        sync={vault.syncState}
        open={navOpen}
        onOpenSettings={() => setShowSettings(true)}
        onLock={vault.lock}
        onSync={async () => {
          await vault.sync();
          const { status, error } = vault.syncState;
          if (status === "ok") toast.success("Synced");
          else toast.warn(error || "Couldn't reach the server — your changes are saved locally.");
        }}
      />

      <div className="main">
        <header className="topbar">
          <button className="icon-btn nav-toggle" onClick={() => setNavOpen((o) => !o)}
            aria-label="Toggle navigation" aria-expanded={navOpen}>
            <MenuIcon />
          </button>

          <div>
            <h1>{meta?.label || "AIDIA"}</h1>
            <div className="sub">
              {showsNotes
                ? `${visibleEntries.length} ${visibleEntries.length === 1 ? "entry" : "entries"}`
                : "Everything encrypted on this device"}
            </div>
          </div>

          <div className="topbar-tools">
            {showsNotes && (
              <>
                <div className="search-box">
                  <SearchIcon size={15} />
                  <input type="search" value={query} placeholder="Search everything…"
                    onChange={(e) => setQuery(e.target.value)} />
                </div>
                {section === "all" && (
                  <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ width: 130 }}>
                    <option value="all">All categories</option>
                    <option value="diary">Diary</option>
                    <option value="journal">Journal</option>
                    <option value="voice">Voice Notes</option>
                    <option value="password">Passwords</option>
                  </select>
                )}
                <div className="segmented">
                  {VIEW_MODES.map((m) => (
                    <button key={m.id} aria-pressed={view === m.id} onClick={() => setView(m.id)}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <button className="icon-btn" onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}>
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>

            {showsNotes && (
              <button className="btn btn-primary" onClick={() => setEditing({})}>
                <PlusIcon size={15} /> New
              </button>
            )}
          </div>
        </header>

        <main className="view">
          {showsNotes && (
            <div className={sideCalendar ? "view-split" : ""}>
              <div>
                {selectedDay && sideCalendar && (
                  <div className="row" style={{ marginBottom: "var(--s-4)" }}>
                    <button className="chip" onClick={() => setSelectedDay(null)}>
                      <CalendarIcon size={13} /> {fmtDate(new Date(selectedDay))} · show all days ×
                    </button>
                  </div>
                )}

                {visibleEntries.length === 0 ? (
                  <EmptyState
                    icon={section === "alerts" ? BellIcon : SearchIcon}
                    title={
                      query ? "Nothing matches that search"
                        : section === "alerts" ? "No reminders due"
                        : "Nothing here yet"
                    }
                    action={
                      !query && section !== "alerts" ? (
                        <button className="btn btn-primary" onClick={() => setEditing({})}>
                          <PlusIcon size={15} /> Write the first one
                        </button>
                      ) : null
                    }
                  >
                    {query
                      ? "Try a different word, or widen the category filter."
                      : section === "alerts"
                      ? "Dates you mention in an entry are detected automatically and offered as reminders."
                      : "Entries you write are encrypted on this device before they are stored."}
                  </EmptyState>
                ) : (
                  <EntryList entries={visibleEntries} query={query} view={view}
                    onOpen={(entry) => setEditing(entry)} />
                )}
              </div>

              {sideCalendar && (
                <aside className="panel">
                  <MiniCalendar month={month} onMonth={setMonth} selected={selectedDay}
                    onSelect={setSelectedDay} entriesByDay={entriesByDay} compact />
                </aside>
              )}
            </div>
          )}

          {section === "calendar" && (
            <div className="view-split calendar-major">
              <div>
                <h3 style={{ marginBottom: "var(--s-4)" }}>
                  {selectedDay ? fmtDate(new Date(selectedDay)) : "Select a day"}
                </h3>
                {selectedDay && (entriesByDay[selectedDay]?.length ?? 0) > 0 ? (
                  <EntryList entries={entriesByDay[selectedDay]} view={view}
                    onOpen={(entry) => setEditing(entry)} />
                ) : (
                  <EmptyState icon={CalendarIcon} title={selectedDay ? "Nothing written that day" : "Pick a day"}>
                    {selectedDay
                      ? "Choose another day, or start an entry for this one."
                      : "Days with entries are marked with a coloured dot."}
                  </EmptyState>
                )}
              </div>
              <aside className="panel">
                <MiniCalendar month={month} onMonth={setMonth} selected={selectedDay}
                  onSelect={setSelectedDay} entriesByDay={entriesByDay} />
              </aside>
            </div>
          )}

          {section === "askai" && <AskAIView onOpenEntry={(e) => setEditing(e)} />}
          {section === "financials" && <FinancialsView />}
          {section === "goals" && <GoalsView />}
        </main>
      </div>

      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}

      {editing && (
        <Editor
          entry={editing.id ? editing : null}
          defaultCategory={NOTE_SECTIONS.includes(section) && section !== "all" && section !== "alerts" ? section : "diary"}
          onClose={() => setEditing(null)}
        />
      )}

      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
    </div>
  );
}

/** Chooses between the lock screen and the app proper. */
export function Root() {
  const { unlocked } = useVault();
  const { theme, toggle } = useTheme();
  return unlocked ? <App /> : <LockScreen theme={theme} onToggleTheme={toggle} />;
}
